import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, ApiError } from './client';
import { getAuthToken } from '../auth/store';
import { ensureFreshApiToken } from '../auth/msal';
import { useMeQuery } from './engagement-lifecycle';

/**
 * Documents surface (T-0040 / E02-F01 chunked upload, T-0041 parse tree,
 * T-0042 / T-0043 reprocess + reindex, T-0044 classification corrections).
 *
 * The engagement's Documents tab lists every non-deleted document from
 * `GET /engagements/:id/documents` (T-0160) — until then it listed only
 * documents with a classification, so a file that was never read (still
 * processing, or failed) did not appear at all. Classifications enrich the
 * rows where they exist.
 */

// ---- Whose document it is (E19 / T-0215) ------------------------------------

/**
 * The party a document belongs to, which decides what the engine reads from
 * it (E19 design §1):
 *
 *   vendor   the implementer's or software vendor's contracts and reports
 *   avero    Avero's IV&V contract, proposal and reports
 *   client   the client's own material
 *
 * Older rows carried `other` for the client's material; it reads as `client`.
 */
export const DOCUMENT_PARTIES = ['vendor', 'avero', 'client'] as const;
export type DocumentParty = (typeof DOCUMENT_PARTIES)[number];

export const DOCUMENT_PARTY_LABEL: Record<DocumentParty, string> = {
  vendor: 'Vendor',
  avero: 'Avero',
  client: 'Client',
};

// The preprocess gives the schema an `unknown` input type, which breaks
// `apiClient`'s inference for every schema it sits in; typed on the party
// alone, as the other transformed schemas in this file are.
export const DocumentPartySchema = z.preprocess(
  (value) => (value === 'other' ? 'client' : value),
  z.enum(DOCUMENT_PARTIES),
) as unknown as z.ZodType<DocumentParty>;

/**
 * The classifier's hint (design §2): `avero` when the firm is named on page 1
 * of a document tagged `vendor`, else null. Absent from older servers.
 */
export const PartyHintSchema = z.enum(['avero']).nullable().optional();
export type PartyHint = 'avero' | null;

/**
 * T-0120 (PR #142) added a cached `mime_type` on the
 * document_classifications table; the classifier worker writes it from
 * `DocumentVersion.mime_type` at classification time, manual corrections
 * carry it through the supersede chain, and the list DTO falls back to
 * the latest DocumentVersion mime when the cache row is NULL (legacy rows
 * that pre-date the migration). Surfacing it on the list lets the UI gate
 * mime-specific affordances ("Open inline" for xlsx) without a follow-up
 * doc detail fetch.
 */
export const ClassificationItemSchema = z.object({
  document_id: z.string().uuid(),
  document_title: z.string(),
  status: z.string(),
  predicted_type: z.string().nullable().optional(),
  corrected_type: z.string().nullable().optional(),
  confidence: z.number().nullable().optional(),
  classified_at: z.string().nullable().optional(),
  mime_type: z.string().nullable().optional(),
});
export type ClassificationItem = z.infer<typeof ClassificationItemSchema>;

/**
 * Mime-types treated as Excel workbooks for the doc-card "Open inline"
 * affordance. The set mirrors the backend's classifier mime palette.
 */
export const XLSX_MIME_TYPES: ReadonlySet<string> = new Set([
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.ms-excel.sheet.macroEnabled.12',
]);

export function isXlsxClassification(item: ClassificationItem): boolean {
  const mime = (item.mime_type ?? '').toLowerCase();
  return mime.length > 0 && XLSX_MIME_TYPES.has(mime);
}

export const ClassificationListSchema = z.object({
  items: z.array(ClassificationItemSchema),
});
export type ClassificationList = z.infer<typeof ClassificationListSchema>;

/**
 * The usable copy of a file that is already in the engagement (T-0160): what
 * a deduped initiate names so the UI can say "already in this engagement".
 */
export const ExistingDocumentSchema = z.object({
  document_id: z.string().uuid(),
  title: z.string(),
  /** When that copy was uploaded (ISO-8601). */
  uploaded_at: z.string(),
  upload_status: z.string().optional(),
});
export type ExistingDocument = z.infer<typeof ExistingDocumentSchema>;

export const InitiateUploadResultSchema = z.object({
  upload_id: z.string(),
  chunk_size_bytes: z.number().int().positive().optional(),
  /** The API's advertised chunk size (5 MB) — above its 1 MiB body limit; see `uploadFileChunked`. */
  chunk_size: z.number().int().positive().optional(),
  /**
   * Returned for EVERY upload, new documents included — it is not a sign
   * that the server already has the bytes. Only `deduped: true` is.
   */
  document_id: z.string().uuid().optional(),
  version_id: z.string().uuid().optional(),
  /** True only when the engagement already holds a usable copy: send no bytes. */
  deduped: z.boolean().optional(),
  existing_document: ExistingDocumentSchema.optional(),
});
export type InitiateUploadResult = z.infer<typeof InitiateUploadResultSchema>;

export interface InitiateUploadInput {
  project_id: string;
  title: string;
  /** Whose document it is; the upload modal and the wizard ask (T-0215). */
  source_party: DocumentParty;
  byte_size: number;
  mime_type: string;
  content_hash: string;
  /**
   * T-0187: `discovery_transcript` for an interview transcript (T-0176). Base
   * formation and ongoing routing skip it; the API defaults to `general`.
   */
  purpose?: 'discovery_transcript';
}

/**
 * `GET /api/v1/documents/:id/status` (T-0160 real status). The schema used
 * to require an `id` the API never sends and reject its `null`s, so every
 * poll failed validation and the upload modal timed out into a false
 * "Parsed".
 *   - parse_status: pending | parsing | parsed | failed
 *   - index_status: pending | indexing | indexed | failed | partial
 *   - failure_reason: why the read (or indexing) failed, else null
 */
export const DocumentStatusSchema = z.object({
  document_id: z.string().uuid(),
  upload_status: z.string().optional(),
  parse_status: z.string().nullable().optional(),
  index_status: z.string().nullable().optional(),
  failure_reason: z.string().nullable().optional(),
  classification_status: z.string().nullable().optional(),
});
export type DocumentStatus = z.infer<typeof DocumentStatusSchema>;

export const DocumentTreeSchema = z.object({
  document_id: z.string().uuid(),
  markdown_tree: z.string(),
});
export type DocumentTree = z.infer<typeof DocumentTreeSchema>;

/**
 * E18-F01 / T-0211: where a document came from when the connected SharePoint
 * folder took it — the link back to the file. The documents list carries it
 * on every row as `document_source` (null when the document was uploaded;
 * absent from older servers). `source` stays the party (`vendor` / `avero` /
 * `client`). `sharePointSourceOf` reads it.
 */
export const DocumentSharePointSourceSchema = z.object({
  /** `sharepoint` */
  kind: z.string(),
  web_url: z.string(),
  path: z.string().nullable().optional(),
});
export type DocumentSharePointSource = z.infer<typeof DocumentSharePointSourceSchema>;

/** The SharePoint file a document came from, or null for an upload. */
export function sharePointSourceOf(
  doc: { document_source?: DocumentSharePointSource | null } | null | undefined,
): DocumentSharePointSource | null {
  const source = doc?.document_source;
  if (!source) return null;
  return source.kind === 'sharepoint' ? source : null;
}

/**
 * Single-doc detail surface used by the engagement document detail
 * route (T-0036 / E04-F05 — inline Excel rendering). Backed by
 * `GET /api/v1/engagements/:engagement_id/documents/:document_id`.
 *
 * The party arrives as `source_party` (E19 design §5) or, from an older
 * server, as `source`; the parsed detail always carries it as
 * `source_party` (null only when the server sent neither). `party_hint` is
 * the classifier's hint (T-0215).
 */
const DocumentDetailWireSchema = z
  .object({
    id: z.string().uuid(),
    engagement_id: z.string().uuid(),
    name: z.string(),
    mime_type: z.string(),
    byte_size: z.number(),
    source: DocumentPartySchema.optional(),
    source_party: DocumentPartySchema.optional(),
    party_hint: PartyHintSchema,
    upload_status: z.string(),
    is_inline_renderable: z.boolean(),
    created_at: z.string(),
    updated_at: z.string(),
  })
  .passthrough();
export const DocumentDetailSchema = DocumentDetailWireSchema.transform((detail) => ({
  ...detail,
  source_party: detail.source_party ?? detail.source ?? null,
  party_hint: detail.party_hint ?? null,
}));
export type DocumentDetail = z.infer<typeof DocumentDetailSchema>;

/**
 * Engagement-scoped document list (T-0044). Mirrors
 * `apps/api/src/services/documents.ts → DocumentListDTO`. The wizard's
 * baseline-docs step uses this to derive the parse/index status chips
 * for each uploaded file (matching the prototype's `chip-healthy`
 * "Parsed" vs `chip-neutral` "Uploading…" affordance).
 *
 * The endpoint is documented separately from the classifications proxy
 * the doc tab uses; both back the same Document row but expose
 * different projections. v1 process_status enum:
 *   'uploading' | 'pending' | 'extracted' | 'indexed' | 'failed'.
 */
export const DocumentListItemSchema = z.object({
  id: z.string().uuid(),
  engagement_id: z.string().uuid(),
  name: z.string(),
  mime_type: z.string(),
  /** Whose document it is (T-0215). */
  source: DocumentPartySchema,
  /** The classifier's hint: Avero is named on page 1 of a vendor-tagged document. */
  party_hint: PartyHintSchema,
  /** The SharePoint file it came from (T-0211); null for an upload, absent from older servers. */
  document_source: DocumentSharePointSourceSchema.nullable().optional(),
  byte_size: z.number(),
  classification: z.string().nullable(),
  process_status: z.enum(['uploading', 'pending', 'extracted', 'indexed', 'failed']),
  /** `general`, or `discovery_transcript` for an interview transcript. */
  purpose: z.string().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type DocumentListItem = z.infer<typeof DocumentListItemSchema>;

export const DocumentListResponseSchema = z.object({
  documents: z.array(DocumentListItemSchema),
  next_cursor: z.string().optional(),
});
export type DocumentListResponse = z.infer<typeof DocumentListResponseSchema>;

/** The list endpoint's largest page (`DOCS_MAX_LIMIT` in services/documents.ts). */
export const DOCUMENTS_PAGE_SIZE = 200;
/** Paging stops here (20,000 documents) so a cursor fault cannot loop forever. */
export const MAX_DOCUMENT_PAGES = 100;

/**
 * The engagement's documents. With `limit`, one page of that size. Without
 * it, EVERY document (T-0160): the Documents screen lists each non-deleted
 * document from here, so it follows `next_cursor` past the server's default
 * page of 50.
 */
export async function listEngagementDocuments(
  engagementId: string,
  opts: { limit?: number } = {},
): Promise<DocumentListResponse> {
  const path = `/api/v1/engagements/${encodeURIComponent(engagementId)}/documents`;
  if (opts.limit !== undefined) {
    return apiClient(path, DocumentListResponseSchema, { query: { limit: opts.limit } });
  }
  const documents: DocumentListItem[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < MAX_DOCUMENT_PAGES; page += 1) {
    const res = await apiClient(path, DocumentListResponseSchema, {
      query: { limit: DOCUMENTS_PAGE_SIZE, cursor },
    });
    documents.push(...res.documents);
    if (!res.next_cursor) break;
    cursor = res.next_cursor;
  }
  return { documents };
}

/**
 * One page of the engagement's documents whose title contains `q`
 * (case-insensitive, server side), for the command palette (T-0227).
 */
export function searchEngagementDocuments(
  engagementId: string,
  q: string,
  limit: number,
): Promise<DocumentListResponse> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/documents`,
    DocumentListResponseSchema,
    { query: { q, limit } },
  );
}

/**
 * Polling hook used by the wizard's baseline-docs step to refresh the
 * status chips on each row until every doc settles. The page passes
 * `enabled=false` until the engagement is minted; the inline
 * refetchInterval function stops polling once every doc is at a
 * terminal status (`indexed` or `failed`).
 */
export function useEngagementDocumentsQuery(
  engagementId: string | undefined,
  opts?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['engagement-documents', engagementId] as const,
    queryFn: () => listEngagementDocuments(engagementId!),
    enabled: Boolean(engagementId) && (opts?.enabled ?? true),
    refetchInterval: (q) => {
      const data = q.state.data as DocumentListResponse | undefined;
      const docs = data?.documents ?? [];
      if (docs.length === 0) return 3000;
      const anyPending = docs.some(
        (d) => d.process_status !== 'indexed' && d.process_status !== 'failed',
      );
      return anyPending ? 3000 : false;
    },
  });
}

export function listClassifications(
  engagementId: string,
  opts?: { status?: string },
): Promise<ClassificationList> {
  // The list endpoint may return either `{items: [...]}` or a raw array
  // depending on the backend revision; the schema accepts either shape
  // and the wrapper normalizes to the `{items}` form before returning.
  const PermissiveSchema = z.union([ClassificationListSchema, z.array(ClassificationItemSchema)]);
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/documents/classifications`,
    PermissiveSchema,
    { query: opts },
  ).then((v) => (Array.isArray(v) ? { items: v } : v));
}

export function initiateUpload(
  input: InitiateUploadInput,
  signal?: AbortSignal,
): Promise<InitiateUploadResult> {
  return apiClient('/api/v1/documents', InitiateUploadResultSchema, {
    method: 'POST',
    body: input,
    signal,
  });
}

export function putUploadChunk(
  uploadId: string,
  index: number,
  bytes: Uint8Array,
  chunkHash: string,
  signal?: AbortSignal,
): Promise<void> {
  return apiClient<void>(
    `/api/v1/documents/uploads/${encodeURIComponent(uploadId)}/chunks/${index}`,
    z.any().transform(() => undefined) as z.ZodType<void>,
    {
      method: 'PUT',
      body: bytes,
      headers: {
        'Content-Type': 'application/octet-stream',
        'X-Chunk-Hash': chunkHash,
      },
      signal,
    },
  );
}

export function completeUpload(
  uploadId: string,
  signal?: AbortSignal,
): Promise<{ document_id: string; version_id?: string }> {
  return apiClient(
    `/api/v1/documents/uploads/${encodeURIComponent(uploadId)}/complete`,
    z
      .object({ document_id: z.string().uuid(), version_id: z.string().uuid().optional() })
      .passthrough() as unknown as z.ZodType<{ document_id: string; version_id?: string }>,
    { method: 'POST', signal },
  );
}

export function getDocumentStatus(documentId: string): Promise<DocumentStatus> {
  return apiClient(
    `/api/v1/documents/${encodeURIComponent(documentId)}/status`,
    DocumentStatusSchema.passthrough() as unknown as z.ZodType<DocumentStatus>,
  );
}

export function getDocumentTree(documentId: string): Promise<DocumentTree> {
  return apiClient(
    `/api/v1/documents/${encodeURIComponent(documentId)}/tree`,
    DocumentTreeSchema.passthrough() as unknown as z.ZodType<DocumentTree>,
  );
}

export function getDocumentDetail(
  engagementId: string,
  documentId: string,
): Promise<DocumentDetail> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/documents/${encodeURIComponent(
      documentId,
    )}`,
    // The transform normalizes the party, so the schema's input and output
    // differ; `apiClient` types its schema on the output alone.
    DocumentDetailSchema as unknown as z.ZodType<DocumentDetail>,
  );
}

// ---- Changing whose document it is (E19 / T-0215) ---------------------------

/**
 * `PATCH /api/v1/documents/:id` `{ source_party }` answers with the document,
 * how many pending proposals the old party had produced that are now
 * retired, and whether the lead should run extraction again (design §5).
 * The API ships in parallel (T-0213), so the counts default when absent.
 */
export const ChangeDocumentPartyResultSchema = z
  .object({
    document_id: z.string(),
    source_party: DocumentPartySchema.optional(),
    rejected_proposal_count: z.number().int().nonnegative().default(0),
    rerun_extraction: z.boolean().default(true),
  })
  .passthrough();
export type ChangeDocumentPartyResult = z.infer<typeof ChangeDocumentPartyResultSchema>;

function newIdempotencyKey(): string {
  return typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Re-tag a document. Every call mints its own Idempotency-Key: a retry is a new request. */
export function changeDocumentParty(
  documentId: string,
  party: DocumentParty,
): Promise<ChangeDocumentPartyResult> {
  return apiClient(
    `/api/v1/documents/${encodeURIComponent(documentId)}`,
    // `.default()` makes the schema's input and output differ; see above.
    ChangeDocumentPartyResultSchema as unknown as z.ZodType<ChangeDocumentPartyResult>,
    {
      method: 'PATCH',
      body: { source_party: party },
      idempotencyKey: newIdempotencyKey(),
    },
  );
}

export interface ChangeDocumentPartyInput {
  documentId: string;
  party: DocumentParty;
}

/**
 * Change a document's party. On success the documents list (and its
 * processing view), the document's detail and the classifications are
 * refreshed: the chip, the hint and the classification all follow the party.
 */
export function useChangeDocumentPartyMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, party }: ChangeDocumentPartyInput) =>
      changeDocumentParty(documentId, party),
    onSuccess: (_out, { documentId }) => {
      if (engagementId) {
        void qc.invalidateQueries({ queryKey: ['engagement-documents', engagementId] });
        void qc.invalidateQueries({ queryKey: ['document-detail', engagementId, documentId] });
        void qc.invalidateQueries({ queryKey: ['documents', 'classifications', engagementId] });
      }
    },
  });
}

/**
 * Whether the signed-in user may change whose document it is: the API's
 * floor is engagement_lead, which a firm admin clears too. True only once
 * `/me` has said so; a consultant sees the chip and the hint without the
 * controls, and the PATCH route refuses on its own.
 */
export function useCanChangeDocumentParty(): boolean {
  const role = useMeQuery().data?.role;
  return role === 'engagement_lead' || role === 'firm_admin';
}

/**
 * Build the relative URL of the document content stream. Used by the
 * inline Excel viewer (T-0036) and the plain-download fallback. The
 * actual fetch is not piped through `apiClient` because the response
 * body is binary; we use a small raw-fetch helper here.
 */
export function documentContentUrl(engagementId: string, documentId: string): string {
  return `/api/v1/engagements/${encodeURIComponent(engagementId)}/documents/${encodeURIComponent(
    documentId,
  )}/content`;
}

/**
 * Fetch the raw bytes of a document. Used by the inline Excel viewer
 * (T-0036) to feed SheetJS for client-side workbook parsing. Mirrors
 * apiClient's correlation-id + bearer-attach behavior but returns
 * an ArrayBuffer instead of a parsed JSON body.
 *
 * Failure cases:
 *   - 4xx/5xx with a JSON error body → ApiError preserving the error code
 *   - 4xx/5xx with a non-JSON body → ApiError code='internal'
 *   - network failure → ApiError code='network'
 */
export async function fetchDocumentContent(
  engagementId: string,
  documentId: string,
): Promise<ArrayBuffer> {
  const correlationId =
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : `cid-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const headers: Record<string, string> = {
    Accept: 'application/octet-stream',
    'X-Correlation-Id': correlationId,
  };
  const token = (await ensureFreshApiToken()) ?? getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  let res: Response;
  try {
    res = await fetch(documentContentUrl(engagementId, documentId), {
      method: 'GET',
      headers,
    });
  } catch (err) {
    throw new ApiError(
      0,
      'network',
      err instanceof Error ? err.message : 'Network error.',
      correlationId,
    );
  }
  if (!res.ok) {
    // Try to extract the JSON error body the API conventions use.
    try {
      const body = (await res.json()) as {
        error?: { code?: string; message?: string };
      };
      throw new ApiError(
        res.status,
        body.error?.code ?? 'internal',
        body.error?.message ?? `Request failed with status ${res.status}.`,
        correlationId,
      );
    } catch (parseErr) {
      if (parseErr instanceof ApiError) throw parseErr;
      throw new ApiError(
        res.status,
        'internal',
        `Request failed with status ${res.status}.`,
        correlationId,
      );
    }
  }
  return await res.arrayBuffer();
}

/**
 * Read the document again (T-0042 `POST /documents/:id/reprocess`, 202). The
 * API answers `{ extraction_run_id, document_id, document_version_id,
 * status }`; the schema used to require a `run_id` it never sends, so a
 * successful reprocess surfaced as an error.
 */
export function reprocessDocument(
  documentId: string,
): Promise<{ extraction_run_id?: string; run_id?: string }> {
  return apiClient(
    `/api/v1/documents/${encodeURIComponent(documentId)}/reprocess`,
    z
      .object({ extraction_run_id: z.string().optional(), run_id: z.string().optional() })
      .passthrough() as unknown as z.ZodType<{ extraction_run_id?: string; run_id?: string }>,
    { method: 'POST' },
  );
}

// ---- TanStack Query hooks --------------------------------------------------

export function useClassificationsQuery(engagementId: string | undefined, status?: string) {
  return useQuery({
    queryKey: ['documents', 'classifications', engagementId, status ?? null] as const,
    queryFn: () => listClassifications(engagementId!, status ? { status } : {}),
    enabled: Boolean(engagementId),
  });
}

export function useDocumentDetailQuery(
  engagementId: string | undefined,
  documentId: string | undefined,
) {
  return useQuery({
    queryKey: ['document-detail', engagementId, documentId] as const,
    queryFn: () => getDocumentDetail(engagementId!, documentId!),
    enabled: Boolean(engagementId && documentId),
  });
}

/**
 * Live parse-tree hook (T-0041 / T-0114). The backend stores the parsed
 * markdown tree on the Document row; the modal renders it side-by-side
 * with a "stage" view that previews each section. We disable the query
 * by default so the modal only fetches when it actually opens.
 */
export function useDocumentTreeQuery(documentId: string | undefined, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['document-tree', documentId] as const,
    queryFn: () => getDocumentTree(documentId!),
    enabled: Boolean(documentId) && (opts?.enabled ?? true),
    staleTime: 60_000,
  });
}

/**
 * Live parse-/upload-/index-status hook used by the parse-tree modal to
 * show the most recent processing state alongside the markdown preview.
 * Polls every 5s while a status is "in_progress" (cheap probe) and
 * settles to a 30s staleTime once the document is terminal.
 */
export function useDocumentStatusQuery(
  documentId: string | undefined,
  opts?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['document-status', documentId] as const,
    queryFn: () => getDocumentStatus(documentId!),
    enabled: Boolean(documentId) && (opts?.enabled ?? true),
    staleTime: 30_000,
  });
}

export function useReprocessDocumentMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => reprocessDocument(documentId),
    onSuccess: (_out, documentId) => {
      if (engagementId) {
        void qc.invalidateQueries({ queryKey: ['documents', 'classifications', engagementId] });
        void qc.invalidateQueries({ queryKey: ['engagement-documents', engagementId] });
      }
      void qc.invalidateQueries({ queryKey: ['document-status', documentId] });
    },
  });
}

/** Re-queue the failed chunks of a document whose indexing failed. */
export function useReindexDocumentMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => reindexDocument(documentId, 'failed_only'),
    onSuccess: (_out, documentId) => {
      if (engagementId) {
        void qc.invalidateQueries({ queryKey: ['engagement-documents', engagementId] });
      }
      void qc.invalidateQueries({ queryKey: ['document-status', documentId] });
    },
  });
}

// ---- Single-file upload helper --------------------------------------------

/**
 * Compute a SHA-256 hex digest of the given bytes using the browser's
 * Web Crypto API. Used by {@link uploadFileChunked} to satisfy the
 * `content_hash` body field on `POST /api/v1/documents` and the
 * `X-Chunk-Hash` header on each chunk PUT.
 *
 * Returns the lowercase hex string the API expects.
 */
async function sha256Hex(bytes: Uint8Array): Promise<string> {
  // `crypto.subtle.digest` accepts a BufferSource. TS5 narrowed BufferSource
  // to disallow SharedArrayBuffer-backed views, so we slice into a plain
  // ArrayBuffer-backed copy before passing. The cost is one extra
  // allocation per hash — negligible for the ≤MB files this path handles.
  const ab = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', ab as ArrayBuffer);
  const view = new Uint8Array(digest);
  let out = '';
  for (let i = 0; i < view.length; i++) {
    // `noUncheckedIndexedAccess` makes view[i] possibly-undefined; guard
    // narrows it so the toString call type-checks.
    const byte = view[i];
    if (byte === undefined) continue;
    out += byte.toString(16).padStart(2, '0');
  }
  return out;
}

/**
 * The most one upload may be (C2-72). Mirrors `MAX_BYTE_SIZE` and
 * `MAX_UPLOAD_SIZE_LABEL` in `apps/api/src/services/uploads.ts`, which
 * refuses a larger declared size with a 400 that says this label. Every
 * upload surface words the cap from here.
 */
export const MAX_UPLOAD_BYTE_SIZE = 500 * 1024 * 1024;
export const MAX_UPLOAD_SIZE_LABEL = '500 MB';

/**
 * Chunk size. 1 MiB: the API reads each chunk PUT as one request body and
 * Fastify's default body limit is 1 MiB, so this is also the ceiling — the
 * initiate response advertises `chunk_size` 5 MB, which the chunk route
 * would refuse. Small uploads (banner images, screenshots) fit in a single
 * chunk; large ones (SOWs, change orders) get split.
 */
const DEFAULT_CHUNK_SIZE_BYTES = 1024 * 1024;
const MAX_CHUNK_SIZE_BYTES = 1024 * 1024;

export interface UploadFileProgress {
  /** Bytes the server has received so far. */
  sentBytes: number;
  /** The file's size. */
  totalBytes: number;
}

export interface UploadFileOptions {
  /** The Project (engagement parent) id. `POST /api/v1/documents` keys on
   *  project_id; the image-slot consumer must resolve engagement → project
   *  before calling. */
  projectId: string;
  /** Human-readable title persisted on the Document row. Defaults to the
   *  file's name when omitted. */
  title?: string;
  /**
   * Whose document it is (T-0215). Every caller says so: the upload modal
   * and the wizard ask the person; a banner image is Avero's. There is no
   * default — a silent one is how every document became "Avero".
   */
  sourceParty: DocumentParty;
  /**
   * Bytes sent so far: once with 0 after initiate, then after every chunk
   * the server has received, ending at `totalBytes`. The upload modal's
   * progress bar reads this — bytes acknowledged, never an estimate.
   */
  onProgress?: (progress: UploadFileProgress) => void;
  /** Cancels the upload: the request in flight, and every step after it. */
  signal?: AbortSignal;
  /** Stored on the document; see `InitiateUploadInput.purpose`. */
  purpose?: 'discovery_transcript';
}

export interface UploadFileResult {
  document_id: string;
  version_id?: string;
  /**
   * True when the engagement already held a usable copy of this file, so no
   * bytes were sent; `document_id` is that copy's.
   */
  deduped: boolean;
  /** The copy already in the engagement, when `deduped`. */
  existing_document?: ExistingDocument;
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) throw new DOMException('The upload was cancelled.', 'AbortError');
}

/**
 * Drive a single file through the full T-0040 chunked-upload flow — THE
 * upload implementation of the web app (T-0160): the Documents-screen upload
 * modal, the new-engagement wizard and the image slot all call it.
 *   1. POST /api/v1/documents     → upload_id (+ deduped)
 *   2. PUT  /api/v1/documents/uploads/:upload_id/chunks/:index  (EVERY chunk)
 *   3. POST /api/v1/documents/uploads/:upload_id/complete       → document_id
 *
 * Chunks are skipped ONLY when initiate says `deduped: true` — the engagement
 * already holds a usable copy, which the result names. Initiate returns a
 * `document_id` for every upload, new ones included, so its presence means
 * nothing; the modal used to treat it as "already uploaded", send no bytes,
 * and the API stored an empty file. An empty file (0 bytes) has no chunks.
 *
 * Errors propagate as `ApiError` from the underlying `apiClient` calls (a
 * refused complete is a 409 whose message says how many bytes arrived); an
 * aborted upload rejects with an `AbortError`.
 */
export async function uploadFileChunked(
  file: File,
  opts: UploadFileOptions,
): Promise<UploadFileResult> {
  const { signal, onProgress } = opts;
  const bytes = new Uint8Array(await file.arrayBuffer());
  throwIfAborted(signal);
  const contentHash = await sha256Hex(bytes);
  throwIfAborted(signal);
  const totalBytes = bytes.byteLength;

  const initiate = await initiateUpload(
    {
      project_id: opts.projectId,
      title: opts.title ?? file.name,
      source_party: opts.sourceParty,
      byte_size: totalBytes,
      // An empty type would read as "mime_type is required"; the API's
      // answer to an unknown type is the clearer "can't be uploaded".
      mime_type: file.type || 'application/octet-stream',
      content_hash: contentHash,
      ...(opts.purpose ? { purpose: opts.purpose } : {}),
    },
    signal,
  );

  if (initiate.deduped === true && initiate.document_id) {
    // Already in the engagement: send nothing. Completing the deduped
    // session is a no-op that confirms the copy is still there.
    throwIfAborted(signal);
    const out = await completeUpload(initiate.upload_id, signal);
    onProgress?.({ sentBytes: totalBytes, totalBytes });
    return {
      document_id: out.document_id,
      version_id: out.version_id ?? initiate.version_id,
      deduped: true,
      ...(initiate.existing_document ? { existing_document: initiate.existing_document } : {}),
    };
  }

  const chunkSize = Math.min(
    initiate.chunk_size_bytes ?? DEFAULT_CHUNK_SIZE_BYTES,
    MAX_CHUNK_SIZE_BYTES,
  );
  const chunkCount = Math.ceil(totalBytes / chunkSize);
  onProgress?.({ sentBytes: 0, totalBytes });
  for (let i = 0; i < chunkCount; i++) {
    throwIfAborted(signal);
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, totalBytes);
    const slice = bytes.subarray(start, end);
    const chunkHash = await sha256Hex(slice);
    await putUploadChunk(initiate.upload_id, i, slice, chunkHash, signal);
    onProgress?.({ sentBytes: end, totalBytes });
  }
  throwIfAborted(signal);
  const out = await completeUpload(initiate.upload_id, signal);
  return {
    document_id: out.document_id,
    version_id: out.version_id ?? initiate.version_id,
    deduped: false,
  };
}

/**
 * Re-run indexing for a document whose chunks failed (T-0043
 * `POST /documents/:id/reindex`). `failed_only` re-queues the failed chunks.
 */
export function reindexDocument(
  documentId: string,
  mode: 'failed_only' | 'full' = 'failed_only',
): Promise<{ extraction_run_id: string; chunks_queued: number }> {
  return apiClient(
    `/api/v1/documents/${encodeURIComponent(documentId)}/reindex`,
    z
      .object({ extraction_run_id: z.string(), chunks_queued: z.number() })
      .passthrough() as unknown as z.ZodType<{ extraction_run_id: string; chunks_queued: number }>,
    { method: 'POST', body: { mode } },
  );
}

// ===========================================================================
// Document soft-delete (Tier 3)
// ===========================================================================

const DeleteDocumentResultSchema = z
  .object({ id: z.string(), deleted_at: z.string() })
  .passthrough();

/** `details.reason` of the refusal to delete a cited document (E11 rule 26). */
export const DOCUMENT_CITED_REASON = 'document_cited_by_requirements';

export interface CitedDocumentRefusal {
  /** How many requirements cite the document, removed ones included. */
  total: number;
  /** The first few citing requirement numbers; `total` may be larger. */
  requirementNumbers: string[];
}

/**
 * The citing requirements carried by a refused delete, or null when the
 * error is anything else.
 */
export function citedDocumentFromError(err: unknown): CitedDocumentRefusal | null {
  if (!(err instanceof ApiError) || err.code !== 'conflict') return null;
  const details = err.details;
  if (!details || details['reason'] !== DOCUMENT_CITED_REASON) return null;
  const numbers = Array.isArray(details['requirement_numbers'])
    ? details['requirement_numbers'].filter((n): n is string => typeof n === 'string')
    : [];
  const total = typeof details['total'] === 'number' ? details['total'] : numbers.length;
  return { total: Math.max(total, numbers.length), requirementNumbers: numbers };
}

export function useDeleteDocumentMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      apiClient(
        `/api/v1/engagements/${encodeURIComponent(engagementId!)}/documents/${encodeURIComponent(documentId)}`,
        DeleteDocumentResultSchema,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['engagement-documents', engagementId] });
    },
  });
}
